import React, { useState, useMemo, useRef } from "react";
import { obtenerMovtosCliente, obtenerSiguienteFolioCP, aplicarPago } from "../services/pagosService";
import { buscarClientePorClave, obtenerClientes } from "../services/clienteService";
import { obtenerTipoCambioBanxico } from "../services/facturaService";
import Toast, { useToast } from "../components/Toast";
import PageHeader from "../components/PageHeader";

const fmt2 = (n) =>
  (Number(n) || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return String(d).slice(0, 10);
  }
};

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const TIPO_DOC_PAGO = [
  { v: "TRF", l: "TRF – Transferencia" },
  { v: "EFE", l: "EFE – Efectivo" },
  { v: "CH", l: "CH – Cheque" },
  { v: "TC", l: "TC – Tarjeta de Credito" },
  { v: "TD", l: "TD – Tarjeta de Debito" },
  { v: "99", l: "99 – Otro" },
];

const movKey = (m) => `${m.tipo}-${m.serie}-${m.factura}`;

export default function PagosClientesPage() {
  const toast = useToast();
  const empresaId = Number(localStorage.getItem("empresaId") || 1);

  const [form, setForm] = useState({
    cliente: "",
    nombreCliente: "",
    fechaPago: localToday(),
    tipoDocPago: "TRF",
    numDocPago: "",
    monedaPago: "MXN",
    tipoCambio: 1,
  });

  const [folio, setFolio] = useState(null);
  const [movtos, setMovtos] = useState([]);
  const [importes, setImportes] = useState({});
  const [seleccionados, setSeleccionados] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clienteLoading, setClienteLoading] = useState(false);
  const [generarTimbre, setGenerarTimbre] = useState(true);
  const [sugerencias, setSugerencias] = useState([]);
  const [mostrarSug, setMostrarSug] = useState(false);
  const debounceRef = useRef(null);
  const [tcLoading, setTcLoading] = useState(false);
  const [tcMsg, setTcMsg] = useState("");

  const usarTipoCambioBanxico = async (fecha) => {
    setTcLoading(true);
    setTcMsg("");
    try {
      const { fecha: fechaUsada, valor } = await obtenerTipoCambioBanxico(fecha || form.fechaPago);
      setForm((f) => ({ ...f, tipoCambio: valor }));
      setTcMsg(fechaUsada === (fecha || form.fechaPago) ? "" : `Sin publicación ese día; se usó el FIX del ${fechaUsada}.`);
    } catch (err) {
      setTcMsg(err?.response?.data?.message || "No se pudo obtener el tipo de cambio de Banxico.");
    } finally {
      setTcLoading(false);
    }
  };

  const resetPantalla = () => {
    setForm({
      cliente: "",
      nombreCliente: "",
      fechaPago: localToday(),
      tipoDocPago: "TRF",
      numDocPago: "",
      monedaPago: "MXN",
      tipoCambio: 1,
    });
    setFolio(null);
    setMovtos([]);
    setImportes({});
    setSeleccionados({});
    setGenerarTimbre(true);
  };

  const onChangeForm = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({
      ...f,
      [name]: name === "tipoCambio" ? Number(value) : value,
    }));
  };

  const onClienteChange = (e) => {
    onChangeForm(e);
    const val = e.target.value.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) { setSugerencias([]); setMostrarSug(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await obtenerClientes(empresaId, 1, 10, val, "A");
        const lista = res?.data?.Data ?? res?.data?.data ?? [];
        setSugerencias(lista);
        setMostrarSug(lista.length > 0);
      } catch { setSugerencias([]); setMostrarSug(false); }
    }, 300);
  };

  const seleccionarSugerencia = (cli) => {
    const clave = cli.Clave ?? cli.clave ?? "";
    const nombre = cli.NombreFiscal ?? cli.nombreFiscal ?? cli.Nombre ?? cli.nombre ?? clave;
    setForm((f) => ({ ...f, cliente: clave, nombreCliente: nombre }));
    setSugerencias([]);
    setMostrarSug(false);
  };

  const onClienteBlur = async () => {
    const clave = form.cliente?.trim().toUpperCase();
    if (!clave) {
      setForm((f) => ({ ...f, nombreCliente: "" }));
      return;
    }
    setClienteLoading(true);
    try {
      const cli = await buscarClientePorClave(empresaId, clave);
      if (!cli) {
        setForm((f) => ({ ...f, nombreCliente: "" }));
        toast.warning("Cliente no encontrado.");
        return;
      }
      setForm((f) => ({
        ...f,
        cliente: clave,
        nombreCliente: cli.nombre ?? cli.Nombre ?? cli.razonSocial ?? "",
      }));
    } catch {
      toast.error("No se pudo validar el cliente.");
    } finally {
      setClienteLoading(false);
    }
  };

  const handleBuscar = async () => {
    if (!form.cliente) return toast.warning("Captura la clave del cliente primero.");
    if (!form.nombreCliente) return toast.warning("El cliente no ha sido validado. Presiona Tab para buscarlo.");
    setLoading(true);
    try {
      const [data, folioCP] = await Promise.all([
        obtenerMovtosCliente(form.cliente, empresaId),
        obtenerSiguienteFolioCP(empresaId),
      ]);
      setMovtos(data || []);
      setFolio(folioCP);
      setImportes({});
      setSeleccionados({});
      if (!data || data.length === 0)
        toast.info("El cliente no tiene documentos pendientes.");
    } catch {
      toast.error("No se pudieron cargar los movimientos.");
    } finally {
      setLoading(false);
    }
  };

  const onCheckRow = (m) => {
    const k = movKey(m);
    const yaChecked = !!seleccionados[k];
    setSeleccionados((s) => ({ ...s, [k]: !yaChecked }));
    if (!yaChecked && !importes[k]) {
      setImportes((i) => ({ ...i, [k]: m.saldo }));
    }
  };

  const onChangeImporte = (m, val) => {
    const k = movKey(m);
    const num = Number(val);
    setImportes((i) => ({ ...i, [k]: isNaN(num) ? 0 : num }));
    // Auto-select if typing an amount
    if (num > 0) setSeleccionados((s) => ({ ...s, [k]: true }));
  };

  const monedaDoc = (m) => (m.moneda === "D" || m.moneda === "USD" ? "D" : "P");

  const totalEnMonedaPago = useMemo(() => {
    return movtos.reduce((sum, m) => {
      const k = movKey(m);
      if (!seleccionados[k]) return sum;
      const imp = Number(importes[k] ?? 0);
      const md = monedaDoc(m);
      // Convert document amount to payment currency for total display
      if (form.monedaPago === "MXN" && md === "D")
        return sum + imp * (form.tipoCambio || 1);
      if (form.monedaPago === "USD" && md === "P")
        return sum + imp / (form.tipoCambio || 1);
      return sum + imp;
    }, 0);
  }, [movtos, seleccionados, importes, form.monedaPago, form.tipoCambio]);

  const numSeleccionados = useMemo(
    () => movtos.filter((m) => seleccionados[movKey(m)]).length,
    [movtos, seleccionados]
  );

  const onGuardar = async () => {
    if (!form.cliente) return toast.warning("Captura la clave del cliente.");
    if (!form.nombreCliente) return toast.warning("El cliente no ha sido validado.");
    if (!form.fechaPago) return toast.warning("Captura la fecha de pago.");
    if (form.tipoCambio <= 0) return toast.warning("El tipo de cambio debe ser mayor a 0.");
    // Un FIX de exactamente 1 pesos/dólar nunca es real — casi siempre significa que se
    // quedó en el valor por default sin capturar el verdadero tipo de cambio del día.
    if (form.monedaPago === "USD" && Number(form.tipoCambio) === 1)
      return toast.warning("El tipo de cambio sigue en 1 — captura el real del día (o usa el botón Banxico).");

    const pagosSeleccionados = movtos
      .filter((m) => seleccionados[movKey(m)] && Number(importes[movKey(m)] || 0) > 0)
      .map((m) => ({
        tipo: m.tipo,
        serie: m.serie,
        factura: m.factura,
        importe: Math.round(Number(importes[movKey(m)]) * 100) / 100,
        moneda: monedaDoc(m),
        saldoAnterior: Number(m.saldo) || 0,
      }));

    if (!pagosSeleccionados.length)
      return toast.warning("Selecciona al menos un documento con importe mayor a 0.");

    setSaving(true);
    try {
      const res = await aplicarPago({
        cliente: form.cliente,
        fechaPago: form.fechaPago,
        tipoDocPago: form.tipoDocPago,
        numDocPago: form.numDocPago || "",
        monedaPago: form.monedaPago,
        tipoCambio: form.tipoCambio,
        folioPago: folio ?? 1,
        importeTotalPago: Math.round(totalEnMonedaPago * 100) / 100,
        empresa: empresaId,
        generarTimbre,
        pagos: pagosSeleccionados,
      });
      if (res.cfdiUuid) {
        toast.success(`Pago CP-${res.folio} aplicado y timbrado. UUID: ${res.cfdiUuid}`);
      } else {
        toast.success(`Pago CP-${res.folio} aplicado correctamente.`);
      }
      resetPantalla();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err.message ||
          "Error al aplicar el pago."
      );
    } finally {
      setSaving(false);
    }
  };

  const cellH = {
    padding: "9px 12px",
    fontSize: "var(--fs-xs)",
    fontWeight: "var(--fw-semibold)",
    color: "var(--clr-text-secondary)",
    textTransform: "uppercase",
    letterSpacing: ".4px",
    whiteSpace: "nowrap",
  };
  const cell = { padding: "8px 12px", verticalAlign: "middle" };

  return (
    <>
      <Toast toasts={toast.toasts} onClose={toast.closeToast} />
      <PageHeader
        title="Pagos de Clientes"
        subtitle="Aplicacion de pagos y abonos a facturas pendientes"
      />

      {/* ── DATOS DEL PAGO ── */}
      <div className="lmd-card" style={{ marginBottom: "var(--sp-4)" }}>
        <div className="lmd-card-header">
          <h3 className="lmd-card-title">Datos del pago</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {folio && (
              <span style={{ fontSize: "var(--fs-sm)", color: "var(--clr-text-muted)" }}>
                Folio:{" "}
                <strong style={{ color: "var(--clr-primary)", fontFamily: "var(--font-mono)" }}>
                  CP-{folio}
                </strong>
              </span>
            )}
            <button
              type="button"
              className="lmd-btn lmd-btn-secondary"
              onClick={resetPantalla}
              title="Limpiar toda la pantalla para un nuevo pago"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M12 5v14M5 12l7-7 7 7"/>
              </svg>
              Nuevo pago
            </button>
          </div>
        </div>
        <div className="lmd-card-body">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
              gap: "var(--sp-4)",
            }}
          >
            {/* Cliente */}
            <div className="lmd-field" style={{ position: "relative" }}>
              <label className="lmd-label">Clave / Nombre Cliente *</label>
              <input
                className="lmd-input"
                name="cliente"
                value={form.cliente}
                onChange={onClienteChange}
                onBlur={() => { setTimeout(() => setMostrarSug(false), 150); onClienteBlur(); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); setMostrarSug(false); onClienteBlur(); }
                  if (e.key === "Escape") { setMostrarSug(false); }
                }}
                placeholder="Clave o nombre del cliente..."
                autoComplete="off"
              />
              {clienteLoading && (
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3, fontSize: "var(--fs-xs)", color: "var(--clr-text-muted)" }}>
                  <div className="lmd-spinner" style={{ width: 11, height: 11 }} /> Buscando...
                </div>
              )}
              {mostrarSug && sugerencias.length > 0 && (
                <div style={{
                  position: "absolute", top: "100%", left: 0, right: 0, zIndex: 200,
                  background: "#fff", border: "1px solid var(--clr-border)",
                  borderRadius: "var(--radius-sm)", boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                  maxHeight: 220, overflowY: "auto",
                }}>
                  {sugerencias.map((cli, i) => {
                    const clave = cli.Clave ?? cli.clave ?? "";
                    const nombre = cli.NombreFiscal ?? cli.nombreFiscal ?? cli.Nombre ?? cli.nombre ?? "";
                    return (
                      <div
                        key={i}
                        onMouseDown={() => seleccionarSugerencia(cli)}
                        style={{
                          padding: "8px 12px", cursor: "pointer",
                          borderBottom: i < sugerencias.length - 1 ? "1px solid var(--clr-border-subtle)" : "none",
                          fontSize: "var(--fs-sm)",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "var(--clr-bg)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "#fff"}
                      >
                        <span style={{ fontWeight: "var(--fw-semibold)", color: "var(--clr-primary)", fontFamily: "var(--font-mono)" }}>{clave}</span>
                        <span style={{ marginLeft: 10, color: "var(--clr-text-secondary)" }}>{nombre}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Nombre */}
            <div className="lmd-field">
              <label className="lmd-label">Nombre</label>
              <input
                className="lmd-input"
                value={form.nombreCliente}
                readOnly
                style={{ background: form.nombreCliente ? "#fff" : "var(--clr-bg)" }}
                placeholder="Se llena al capturar clave"
              />
            </div>

            {/* Fecha */}
            <div className="lmd-field">
              <label className="lmd-label">Fecha Pago *</label>
              <input
                className="lmd-input"
                type="date"
                name="fechaPago"
                value={form.fechaPago}
                onChange={onChangeForm}
              />
            </div>

            {/* Tipo doc */}
            <div className="lmd-field">
              <label className="lmd-label">Tipo Documento</label>
              <select
                className="lmd-select"
                name="tipoDocPago"
                value={form.tipoDocPago}
                onChange={onChangeForm}
                style={{ width: "100%" }}
              >
                {TIPO_DOC_PAGO.map((t) => (
                  <option key={t.v} value={t.v}>{t.l}</option>
                ))}
              </select>
            </div>

            {/* Num doc */}
            <div className="lmd-field">
              <label className="lmd-label">Num Doc Pago</label>
              <input
                className="lmd-input"
                name="numDocPago"
                value={form.numDocPago}
                onChange={onChangeForm}
                placeholder="No. cheque / referencia"
              />
            </div>

            {/* Moneda */}
            <div className="lmd-field">
              <label className="lmd-label">Moneda del Pago</label>
              <select
                className="lmd-select"
                name="monedaPago"
                value={form.monedaPago}
                onChange={(e) => {
                  const monedaPago = e.target.value;
                  setForm((f) => ({ ...f, monedaPago }));
                  if (monedaPago === "USD") usarTipoCambioBanxico();
                }}
                style={{ width: "100%" }}
              >
                <option value="MXN">MXN – Pesos</option>
                <option value="USD">USD – Dolares</option>
              </select>
            </div>

            {/* Tipo cambio */}
            <div className="lmd-field">
              <label className="lmd-label">
                Tipo de Cambio{form.monedaPago === "USD" && <span style={{ color: "var(--clr-danger)" }}> * (va en el CFDI aunque la factura ya esté en USD)</span>}
              </label>
              <div style={{ display: "flex", gap: 6 }}>
                <input
                  className="lmd-input"
                  type="number"
                  name="tipoCambio"
                  value={form.tipoCambio}
                  onChange={onChangeForm}
                  min={0.0001}
                  step="0.0001"
                  style={{ textAlign: "right", flex: 1 }}
                />
                {form.monedaPago === "USD" && (
                  <button
                    type="button"
                    className="lmd-btn lmd-btn-secondary"
                    onClick={() => usarTipoCambioBanxico()}
                    disabled={tcLoading}
                    title="Usar el FIX de Banxico para la fecha de pago"
                    style={{ whiteSpace: "nowrap", fontSize: "var(--fs-xs)", padding: "0 10px" }}
                  >
                    {tcLoading ? "..." : "Banxico"}
                  </button>
                )}
              </div>
              {tcMsg && (
                <p style={{ margin: "2px 0 0", fontSize: 11, color: "var(--clr-text-secondary)" }}>{tcMsg}</p>
              )}
            </div>
          </div>

          {/* Generar timbre */}
          <div style={{ marginTop: "var(--sp-3)", display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="checkbox"
              id="generarTimbre"
              checked={generarTimbre}
              onChange={(e) => setGenerarTimbre(e.target.checked)}
              style={{ width: 16, height: 16, accentColor: "var(--clr-primary)", cursor: "pointer" }}
            />
            <label htmlFor="generarTimbre" style={{ fontSize: "var(--fs-sm)", cursor: "pointer", userSelect: "none" }}>
              Generar complemento de pago (CFDI)
            </label>
          </div>

          {/* Buscar button */}
          <div style={{ marginTop: "var(--sp-4)" }}>
            <button
              className="lmd-btn lmd-btn-primary"
              onClick={handleBuscar}
              disabled={loading}
            >
              {loading ? (
                <><div className="lmd-spinner" style={{ width: 14, height: 14 }} /> Buscando...</>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  Buscar movimientos
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── DOCUMENTOS PENDIENTES ── */}
      {movtos.length > 0 && (
        <div className="lmd-card" style={{ marginBottom: "var(--sp-4)" }}>
          <div className="lmd-card-header">
            <h3 className="lmd-card-title">Documentos pendientes</h3>
            <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
              <span style={{ fontSize: "var(--fs-sm)", color: "var(--clr-text-muted)" }}>
                {movtos.length} documentos
              </span>
              {numSeleccionados > 0 && (
                <span style={{ fontSize: "var(--fs-sm)", fontWeight: "var(--fw-semibold)", color: "var(--clr-primary)" }}>
                  {numSeleccionados} seleccionado{numSeleccionados !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--clr-bg)", borderBottom: "2px solid var(--clr-border)" }}>
                  <th style={{ ...cellH, width: 40, textAlign: "center" }}></th>
                  <th style={{ ...cellH, width: 55 }}>Tipo</th>
                  <th style={{ ...cellH, width: 60 }}>Serie</th>
                  <th style={{ ...cellH, width: 90 }}>Factura</th>
                  <th style={{ ...cellH, width: 110 }}>Fecha</th>
                  <th style={{ ...cellH, width: 110 }}>Vencimiento</th>
                  <th style={{ ...cellH, width: 130, textAlign: "right" }}>Saldo</th>
                  <th style={{ ...cellH, width: 80, textAlign: "center" }}>Moneda</th>
                  <th style={{ ...cellH, width: 160, textAlign: "right" }}>Importe a pagar</th>
                </tr>
              </thead>
              <tbody>
                {movtos.map((m, idx) => {
                  const k = movKey(m);
                  const checked = !!seleccionados[k];
                  const imp = importes[k];
                  const esUSD = monedaDoc(m) === "D";
                  return (
                    <tr
                      key={k}
                      style={{
                        background: checked ? "#eff6ff" : idx % 2 === 0 ? "#fff" : "#fafbfc",
                      }}
                    >
                      <td style={{ ...cell, textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onCheckRow(m)}
                          style={{ width: 16, height: 16, cursor: "pointer", accentColor: "var(--clr-primary)" }}
                        />
                      </td>
                      <td style={cell}>{m.tipo}</td>
                      <td style={cell}>{m.serie}</td>
                      <td style={{ ...cell, fontWeight: "var(--fw-semibold)", color: "var(--clr-primary)" }}>
                        {m.factura}
                      </td>
                      <td style={cell}>{fmtDate(m.fecha)}</td>
                      <td style={cell}>{fmtDate(m.vencimiento)}</td>
                      <td style={{ ...cell, textAlign: "right", fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" }}>
                        {fmt2(m.saldo)}
                      </td>
                      <td style={{ ...cell, textAlign: "center" }}>
                        <span className={`lmd-badge ${esUSD ? "lmd-badge-primary" : "lmd-badge-neutral"}`}>
                          {esUSD ? "USD" : "MXN"}
                        </span>
                      </td>
                      <td style={{ ...cell, textAlign: "right" }}>
                        <input
                          type="number"
                          value={imp ?? ""}
                          onChange={(e) => onChangeImporte(m, e.target.value)}
                          placeholder={fmt2(m.saldo)}
                          style={{
                            width: 140,
                            padding: "5px 8px",
                            border: `1px solid ${checked ? "var(--clr-primary)" : "var(--clr-border)"}`,
                            borderRadius: "var(--radius-sm)",
                            fontSize: "var(--fs-sm)",
                            textAlign: "right",
                            fontFamily: "var(--font-mono)",
                            background: checked ? "#fff" : "var(--clr-bg)",
                            outline: "none",
                          }}
                          step="0.01"
                          min={0}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: "var(--clr-bg)", borderTop: "2px solid var(--clr-border)" }}>
                  <td colSpan={7} style={{ padding: "10px 12px", fontWeight: "var(--fw-semibold)", fontSize: "var(--fs-sm)", textAlign: "right", color: "var(--clr-text-secondary)" }}>
                    Total a pagar ({form.monedaPago}):
                  </td>
                  <td colSpan={2} style={{ padding: "10px 12px", textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: "var(--fw-extrabold)", fontSize: "var(--fs-xl)", color: "var(--clr-primary)" }}>
                    ${fmt2(totalEnMonedaPago)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ── ACCIONES ── */}
      {movtos.length > 0 && (
        <div style={{ display: "flex", gap: "var(--sp-3)", paddingBottom: "var(--sp-8)" }}>
          <button
            className="lmd-btn lmd-btn-primary"
            onClick={onGuardar}
            disabled={saving || numSeleccionados === 0}
          >
            {saving ? (
              <><div className="lmd-spinner" style={{ width: 14, height: 14 }} /> Guardando...</>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                Guardar pago
                {numSeleccionados > 0 && ` (${numSeleccionados})`}
              </>
            )}
          </button>
          <button
            className="lmd-btn lmd-btn-secondary"
            onClick={() => { setSeleccionados({}); setImportes({}); }}
            disabled={saving}
          >
            Limpiar seleccion
          </button>
        </div>
      )}
    </>
  );
}
